import { styled, keyframes } from 'goober';
import { appColors } from '@base/useTheme';
import { PipelineStepStatus } from './parsePipelineLogs';

export const STATUS_COLORS: Record<PipelineStepStatus, string> = {
    PENDING: appColors.neutral.w300,
    RUNNING: appColors.primary.w500,
    OK: appColors.success.w500,
    EXTRACTED: appColors.success.w500,
    LOADED: appColors.success.w500,
    EXPORTED: appColors.success.w500,
    FAILED: appColors.error.w500,
    SKIPPED: appColors.neutral.w500,
};

export const STATUS_BG_COLORS: Record<PipelineStepStatus, string> = {
    PENDING: appColors.neutral.w50,
    RUNNING: appColors.primary.w50,
    OK: appColors.success.w50,
    EXTRACTED: appColors.success.w50,
    LOADED: appColors.success.w50,
    EXPORTED: appColors.success.w50,
    FAILED: appColors.error.w50,
    SKIPPED: appColors.neutral.w50,
};

const runningColor = appColors.primary.w500;
const pulse = keyframes`
    0% { box-shadow: 0 0 0 0 ${runningColor}66; }
    70% { box-shadow: 0 0 0 8px ${runningColor}00; }
    100% { box-shadow: 0 0 0 0 ${runningColor}00; }
`;

export const SummaryBar = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.space.l,
    padding: `${theme.space.m} ${theme.space.l}`,
    fontSize: '12px',
    color: theme.colors.neutral.w700,
    borderBottom: `1px solid ${theme.colors.neutral.w200}`,
}));

export const GraphScroller = styled('div')(() => ({
    overflowX: 'auto',
    overflowY: 'auto',
}));

export const GraphContainer = styled('div')(() => ({
    position: 'relative' as const,
    minWidth: 'fit-content',
}));

export const StepNodeContainer = styled('div')<{ $status: PipelineStepStatus; $selected?: boolean }>(
    ({ $status, $selected }) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '12px',
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: '8px',
        border: `1px solid ${STATUS_COLORS[$status]}`,
        borderLeft: `4px solid ${STATUS_COLORS[$status]}`,
        backgroundColor: STATUS_BG_COLORS[$status],
        cursor: 'pointer',
        outline: $selected ? `2px solid ${STATUS_COLORS[$status]}` : 'none',
        outlineOffset: '2px',
        animation: $status === 'RUNNING' ? `${pulse} 2s infinite` : 'none',
        transition: 'outline 0.15s ease',
        '&:hover': {
            filter: 'brightness(0.97)',
        },
    })
);

export const StepName = styled('div')(() => ({
    fontWeight: 700,
    fontSize: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}));

export const StepDetail = styled('div')(({ theme }) => ({
    fontSize: '11px',
    color: theme.colors.neutral.w700,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
}));

export const StepStatusBadge = styled('span')<{ $status: PipelineStepStatus }>(({ $status }) => ({
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 6px',
    borderRadius: '4px',
    color: STATUS_COLORS[$status],
    backgroundColor: 'rgba(255,255,255,0.6)',
    textTransform: 'uppercase',
}));

export const StepWarningBadge = styled('span')(({ theme }) => ({
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 6px',
    borderRadius: '4px',
    color: theme.colors.warning.w700,
    backgroundColor: theme.colors.warning.w50,
    border: `1px solid ${theme.colors.warning.w300}`,
    cursor: 'help',
}));

export const StepWarningTooltipBody = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxWidth: '480px',
}));

export const StepError = styled('div')(({ theme }) => ({
    fontSize: '10px',
    color: theme.colors.error.w700,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: '2px',
}));

export const StepSourceLabel = styled('div')(({ theme }) => ({
    fontSize: '9px',
    fontStyle: 'italic',
    color: theme.colors.neutral.w500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: '2px',
}));

export const EmptyState = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.space.xl,
    color: theme.colors.neutral.w500,
    fontSize: '12px',
}));
